<template>
  <div v-if="taxonomyItems.length">
    <label
      :for="taxonomy"
      class="heading-font text-sm uppercase font-normal mg-color-secondary-lighter"
      >{{ label }}</label
    >
    <div class="relative">
      <div
        @click="toggleDropdown"
        class="mg-form-control mg-select text-sm w-full rounded-lg cursor-pointer flex items-center justify-between"
        :id="taxonomy"
        tabindex="0"
        @keydown.enter="toggleDropdown"
        @keydown.space.prevent="toggleDropdown"
      >
        <span v-if="selectedItems.length === 0" class="text-base"
          >Select options</span
        >
        <span v-else class="truncate text-base"
          >{{ selectedItems.length }} selected</span
        >
        <div
          class="pointer-events-none absolute pin-y pin-r flex items-center px-2 mg-color-accent-2"
        >
          <svg
            class="fill-current h-8 w-8 mg-color-accent-2"
            :class="{ 'transform rotate-180': isOpen }"
          >
            <use href="#icon-angle-down" xlink:href="#icon-angle-down" />
          </svg>
        </div>
      </div>

      <div
        v-show="isOpen"
        class="absolute z-50 w-full bg-white border rounded-lg shadow-lg mt-1 py-2 max-h-60 overflow-y-auto"
      >
        <div
          v-for="(item, index) in taxonomyItems"
          :key="index"
          class="flex items-start px-4 py-2 hover:bg-gray-100"
        >
          <input
            type="checkbox"
            :id="`${taxonomy}-${item.id}`"
            :checked="isSelected(item.id)"
            @change="toggleSelection(item.id)"
            class="mr-3 h-4 w-4 cursor-pointer"
          />
          <label
            :for="`${taxonomy}-${item.id}`"
            class="flex-1 cursor-pointer mb-0"
          >
            <span
              class="text-base leading-none block"
              v-html="item.name"
            ></span>
          </label>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="mg-banded-accent-2-lightest mg-color-accent-2 py-2 px-4 rounded-lg text-sm"
  >
    loading options...
  </div>
</template>

<script>
  import { mapState, mapMutations, mapActions } from "vuex";
  import eventHub from "../event-hub";

  import getQueryParam from "../utilities/get-query-param";

  export default {
    props: ["taxonomy", "label", "feedType"],
    name: "taxonomy-multiselect",
    data() {
      return {
        parentName: this.$parent.$options.name,
        selectedItems: [],
        isOpen: false,
      };
    },
    computed: {
      ...mapState({
        taxonomyItems(state) {
          if (this.taxonomy == "commodity") {
            return state.tax_commodities;
          } else if (this.taxonomy == "state") {
            return state.state;
          } else if (this.taxonomy == "region") {
            return state.regions;
          } else if (this.taxonomy == "topic") {
            return state.topics;
          } else if (this.taxonomy == "country") {
            return state.tax_countries;
          } else if (this.taxonomy == "categories") {
            return state.categories;
          } else if (this.taxonomy == "member_type") {
            return state.member_type;
          } else if (this.taxonomy == "member_area_of_business") {
            return state.member_area_of_business;
          }
        },
      }),
    },
    watch: {
      taxonomyItems: {
        handler(newItems) {
          // When taxonomy items are loaded, check for predefined filters
          if (
            newItems &&
            newItems.length > 0 &&
            this.selectedItems.length === 0
          ) {
            this.checkPredefinedFilter();
          }
        },
        immediate: false,
      },
    },
    methods: {
      ...mapMutations(["updateUrlHashFilter"]),
      ...mapActions([
        "getTaxonomy",
        "getNewsroomFeed",
        "getReportsFeed",
        "handleFilters",
      ]),
      toggleDropdown() {
        this.isOpen = !this.isOpen;
      },
      isSelected(id) {
        return this.selectedItems.includes(id);
      },
      toggleSelection(id) {
        const index = this.selectedItems.indexOf(id);
        if (index > -1) {
          this.selectedItems.splice(index, 1);
        } else {
          this.selectedItems.push(id);
        }
        this.selectChange();
      },
      selectChange() {
        const payload = {
          type: this.parentName,
          taxonomy: this.taxonomy,
          selected: this.selectedItems,
        };

        this.handleFilters({ ...payload });

        let h = {};
        h[this.taxonomy] = this.selectedItems.join(",");
        this.updateUrlHashFilter(h);

        if (this.parentName != "member-filter") {
          // Call the appropriate feed based on feedType prop
          if (this.feedType === "reports") {
            this.getReportsFeed();
          } else {
            this.getNewsroomFeed();
          }
        }
      },
      resetSelected() {
        this.selectedItems = [];
        this.isOpen = false;
      },
      checkPredefinedFilter() {
        let urlFilter = location.hash.substring(1);

        if (!urlFilter) return;

        urlFilter = `?${urlFilter}`;
        const filterValue = getQueryParam(this.taxonomy, urlFilter);

        if (filterValue) {
          // Handle comma-separated values for multiselect
          const values = filterValue
            .split(",")
            .map((v) => parseInt(v))
            .filter((v) => !isNaN(v));

          if (values.length > 0) {
            this.selectedItems = values;

            const payload = {
              type: this.parentName,
              taxonomy: this.taxonomy,
              selected: values,
            };

            this.handleFilters({ ...payload });
          }
        }
      },
      handleClickOutside(event) {
        if (!this.$el.contains(event.target)) {
          this.isOpen = false;
        }
      },
    },
    created() {
      this.checkPredefinedFilter();

      if (this.taxonomy != "year") {
        this.getTaxonomy(this.taxonomy);
      }

      eventHub.$on("form-reset", this.resetSelected);
    },
    mounted() {
      document.addEventListener("click", this.handleClickOutside);
    },
    // It's good to clean up event listeners before
    // a component is destroyed.
    beforeDestroy: function() {
      eventHub.$off("form-reset", this.resetSelected);
      document.removeEventListener("click", this.handleClickOutside);
    },
  };
</script>

<style scoped>
  /* Ensure dropdown appears above other content */
  .z-50 {
    z-index: 50;
  }
</style>



// WEBPACK FOOTER //
// src/js/vue-components/taxonomy-multiselect.vue